// /common/scrollUtils.js

/**
 * スクロールで表示切替（KVを越えたら出現）
 */
export function toggleElementsByPosition(header, mvSection, elementsToToggle) {
  const kvBottom = mvSection?.offsetHeight || 0;
  const scrolled = window.scrollY > kvBottom;

  elementsToToggle.forEach(({ element }) => {
    if (!element) return;
    element.classList.toggle("active", scrolled);
    element.classList.toggle("hidden", !scrolled);
  });

  if (header) header.classList.toggle("active", scrolled);
}

/**
 * ヘッダー高さに応じて調整（CSS変数へ反映）
 */
export function adjustByHeaderHeight(header, mvSection, menuBg) {
  if (!header) return;
  const headerHeight = header.offsetHeight;
  document.documentElement.style.setProperty("--header-h", `${headerHeight}px`);
  if (mvSection) mvSection.style.marginBlockStart = `${headerHeight}px`;
  if (menuBg) menuBg.style.paddingBlockStart = `${headerHeight * 2}px`;
}

/**
 * ヘッダー高さの変化に追従（リサイズ・内容変化）
 */
export function observeHeaderHeight(header, mvSection, menuBg) {
  if (!header) return;
  const update = () => adjustByHeaderHeight(header, mvSection, menuBg);

  update();
  window.addEventListener("resize", update, { passive: true });
  if ("ResizeObserver" in window) {
    const ro = new ResizeObserver(update);
    ro.observe(header);
  }
}

/* ----------------
   スクロール安定化
----------------- */

function getHeaderOffset() {
  const rootStyle = getComputedStyle(document.documentElement);
  return parseFloat(rootStyle.getPropertyValue("--header-h")) || 0;
}

function waitForStableLayout({ maxWait = 1200, settle = 100 } = {}) {
  return new Promise((resolve) => {
    const start = performance.now();
    let lastH = document.documentElement.scrollHeight;
    let lastT = start;
    const tick = (now) => {
      const h = document.documentElement.scrollHeight;
      if (h === lastH && now - lastT >= settle) return resolve();
      if (h !== lastH) {
        lastH = h;
        lastT = now;
      }
      if (now - start >= maxWait) return resolve();
      requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  });
}

function scrollWithHeaderOffset(el, smooth = true) {
  const headerH = getHeaderOffset();
  const y = Math.max(0, el.getBoundingClientRect().top + window.scrollY - headerH);
  window.scrollTo({ top: y, behavior: smooth ? "smooth" : "auto" });

  // 追いスクロール（メニュー閉鎖やスクロールバー復帰での微ズレを吸収）
  requestAnimationFrame(() => {
    window.scrollTo({ top: y, behavior: "auto" });
  });
}

/**
 * 指定IDへスクロール（固定ヘッダー分を考慮）
 * - URLに # を残さない
 * - reduce motion 配慮
 */
export async function scrollToId(id, { keepHash = false } = {}) {
  if (!id) return false;
  const el = document.getElementById(id);
  if (!el) return false;

  await waitForStableLayout(); // 直前のDOM変化（メニュー閉鎖等）をやり過ごす

  const reduce = matchMedia("(prefers-reduced-motion: reduce)").matches;
  scrollWithHeaderOffset(el, !reduce);

  if (!keepHash) {
    history.replaceState(null, "", location.pathname + location.search);
  }
  return true;
}

/**
 * 同一ページ内アンカークリックを横取りしてスクロール（URLに # を残さない）
 * ※ ヘッダー内ナビは index.js 側で専用制御するため、本文内リンク向け
 */
export function initAnchorLinkInterceptor(root = document) {
  root.addEventListener("click", async (e) => {
    const a = e.target.closest('a[href^="#"]');
    if (!a) return;
    const id = a.getAttribute("href").slice(1);
    if (!id) return;

    const ok = await scrollToId(id, { keepHash: false });
    if (ok) e.preventDefault();
  });
}

/**
 * 別ページから #付きで着地したときの安定スクロール
 * - 画像/フォント読み込み完了後
 * - レイアウトが安定するまで待機
 * - 最終スクロール
 */
export function handleHashOnLoad() {
  if (!location.hash) return;
  const id = decodeURIComponent(location.hash.slice(1));
  if (!id) return;

  const run = async () => {
    // Adobe Fonts / 画像読み込み待機（対応ブラウザのみ有効）
    const imgPromises = Array.from(document.images).map((img) => (img.decode ? img.decode().catch(() => {}) : Promise.resolve()));
    const fontReady = (document.fonts && document.fonts.ready) || Promise.resolve();
    await Promise.allSettled([fontReady, ...imgPromises]);

    await scrollToId(id, { keepHash: false });
  };

  if (document.readyState === "complete") requestAnimationFrame(run);
  else window.addEventListener("load", () => requestAnimationFrame(run), { once: true });
}
